import { useEffect, useState } from 'react';
import { continueRender, delayRender } from 'remotion';
import getFontData from '@/lib/get-font-data';
import { bumpFontLoadVersion } from '@/lib/fonts/fontLoadVersion';

/** Load a catalog/custom font before rendering. Prefers fontUrl (offline
 * render bundles can't resolve catalog ids — see CLAUDE.md text-layer notes);
 * falls back to getFontData(fontName). No fontName = nothing to load.
 *
 * Returns whether the current font has settled (loaded, or failed and left
 * on the fallback), so a caller that MEASURES text (the quote carousel's
 * font fit) can re-measure in the real font. */
export const useLayerFont = (fontName?: string, fontUrl?: string | null): boolean => {
  const [handle] = useState(() => delayRender());
  const key = `${fontName ?? ''}|${fontUrl ?? ''}`;
  const [settledKey, setSettledKey] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!fontName) {
          // nothing to load
        } else if (fontUrl) {
          const font = new FontFace(fontName, `url(${fontUrl})`);
          await font.load();
          if (!cancelled) document.fonts.add(font);
        } else {
          const fontData = await getFontData(fontName);
          if (!cancelled && fontData) {
            const font = new FontFace(fontData.id, `url(${fontData.url})`);
            await font.load();
            document.fonts.add(font);
          }
        }
      } catch {
        // keep the sans-serif fallback
      }
      if (!cancelled) {
        setSettledKey(`${fontName ?? ''}|${fontUrl ?? ''}`);
        // Text measured outside the composition (drag overlay) re-measures.
        bumpFontLoadVersion();
      }
      continueRender(handle);
    })();
    return () => {
      cancelled = true;
    };
  }, [fontName, fontUrl, handle]);
  return settledKey === key;
};
